import { apiClient } from "../../lib/apiClient";
import type { BotSettingsPatch } from "./config/mergePatch";
import { fileDownloader } from "../../lib/download";
import { ApiError, describeError, errorReason } from "../../lib/errors";
import type {
  ActivityResponse,
  ApplyProfileMode,
  ApplyProfileResult,
  CreateGuildsInput,
  CreateGuildsPlan,
  GuildTemplate,
  GuildTemplateImportResult,
  GuildTemplateInput,
  GuildTemplatesResponse,
  ImportResult,
  OverviewResponse,
  Phrase,
  PhrasesResponse,
  PoolResponse,
  RosterImportMode,
  RosterImportResult,
  RosterInput,
  RosterResponse,
  RosterRow,
} from "./types";

const base = (projectId: string) =>
  `/api/admin/projects/${encodeURIComponent(projectId)}/bots`;

/**
 * The four list endpoints of this page take the same kind of parameter bag —
 * a search box, a couple of pickers, a page size — and all four drop the ones
 * nobody has filled in. An empty string is «no filter» rather than «match the
 * empty value», which is what a cleared Select means on every one of these
 * screens; sending it would narrow the answer to nothing.
 */
function query(params: object): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  return qs.toString() ? `?${qs}` : "";
}

/** One refusal both import endpoints share, in one sentence. */
const BODY_TOO_LARGE =
  "That file is too large to import in one go. Split it and import the halves.";

export function getBotsOverview(projectId: string): Promise<OverviewResponse> {
  return apiClient.get<OverviewResponse>(`${base(projectId)}/overview`).then((r) => r.data);
}

export interface PoolParams {
  q?: string;
  profile?: string;
  state?: string;
  guild_id?: string;
  limit?: number;
  offset?: number;
}

export function listBotPool(
  projectId: string,
  params: PoolParams = {},
): Promise<PoolResponse> {
  const suffix = query(params);
  return apiClient.get<PoolResponse>(`${base(projectId)}/pool${suffix}`).then((r) => r.data);
}

export function setBotEnabled(
  projectId: string,
  botId: string,
  enabled: boolean,
): Promise<void> {
  return apiClient
    .patch<void>(`${base(projectId)}/pool/${encodeURIComponent(botId)}`, { enabled })
    .then(() => undefined);
}

/**
 * Run the configured profile rules over the pool that already exists. The
 * worker applies them to every bot it creates from now on; this is the only
 * way to reach the ones created before the rules were written.
 */
export function applyBotProfile(
  projectId: string,
  mode: ApplyProfileMode,
): Promise<ApplyProfileResult> {
  return apiClient
    .post<ApplyProfileResult>(`${base(projectId)}/pool/apply-profile`, { mode })
    .then((r) => r.data);
}

export function removeBot(projectId: string, botId: string): Promise<void> {
  return apiClient
    .delete<void>(`${base(projectId)}/pool/${encodeURIComponent(botId)}`)
    .then(() => undefined);
}

export interface ActivityParams {
  kind?: string;
  guild_id?: string;
  q?: string;
  window?: "1h" | "24h" | "7d";
}

export function listBotActivity(
  projectId: string,
  params: ActivityParams = {},
): Promise<ActivityResponse> {
  const suffix = query(params);
  return apiClient
    .get<ActivityResponse>(`${base(projectId)}/activity${suffix}`)
    .then((r) => r.data);
}

export function listPhrases(
  projectId: string,
  category?: string,
  locale?: string,
): Promise<PhrasesResponse> {
  const qs = new URLSearchParams();
  if (category) qs.set("category", category);
  if (locale) qs.set("locale", locale);
  const suffix = qs.toString() ? `?${qs}` : "";
  return apiClient
    .get<PhrasesResponse>(`${base(projectId)}/phrases${suffix}`)
    .then((r) => r.data);
}

export function createPhrase(
  projectId: string,
  category: string,
  locale: string,
  text: string,
): Promise<Phrase> {
  return apiClient
    .post<Phrase>(`${base(projectId)}/phrases`, { category, locale, text })
    .then((r) => r.data);
}

export function updatePhrase(
  projectId: string,
  phraseId: string,
  patch: { text?: string; status?: "live" | "paused" },
): Promise<void> {
  return apiClient
    .patch<void>(`${base(projectId)}/phrases/${encodeURIComponent(phraseId)}`, patch)
    .then(() => undefined);
}

export function deletePhrase(projectId: string, phraseId: string): Promise<void> {
  return apiClient
    .delete<void>(`${base(projectId)}/phrases/${encodeURIComponent(phraseId)}`)
    .then(() => undefined);
}

export interface ImportInput {
  mode: "lines" | "csv";
  body: string;
  category?: string;
  locale?: string;
  /** Parse and report without writing — what the modal's preview table shows,
   *  including the rows it will skip and why. */
  preview?: boolean;
}

export function importPhrases(
  projectId: string,
  input: ImportInput,
): Promise<ImportResult> {
  return apiClient
    .post<ImportResult>(`${base(projectId)}/phrases/import`, input)
    .then((r) => r.data);
}

/** Run one simulation sweep now instead of waiting for the next one. */
export function runBotTick(projectId: string): Promise<void> {
  return apiClient.post<void>(`${base(projectId)}/tick`).then(() => undefined);
}

/**
 * Turn whatever a mutation threw into a sentence. Every call site on this page
 * needs the same two lines, and an unknown failure still has to say something.
 */
export function botError(err: unknown): string {
  return describeError(err instanceof ApiError ? err.code : "internal");
}

/* --- the Source tab: bots.source and the bot_roster table ----------------- */

export interface RosterParams {
  q?: string;
  status?: string;
  country?: string;
  limit?: number;
  offset?: number;
}

/**
 * One page of the table plus the runway strip above it. The two travel
 * together because the tab draws them together — see the handler, which says
 * the same thing from the other side.
 */
export function listRoster(
  projectId: string,
  params: RosterParams = {},
): Promise<RosterResponse> {
  const suffix = query(params);
  return apiClient
    .get<RosterResponse>(`${base(projectId)}/roster${suffix}`)
    .then((r) => r.data);
}

export function createRosterRow(
  projectId: string,
  input: RosterInput,
): Promise<RosterRow> {
  return apiClient
    .post<RosterRow>(`${base(projectId)}/roster`, input)
    .then((r) => r.data);
}

/**
 * Replace a row's editable fields — every field RosterInput declares, and the
 * PATCH replaces all of them. They travel all of them and every time: the
 * dialog shows every one and «Save row» means what is on the screen, so a
 * request that omitted one would be the browser deciding to keep a value the
 * operator had just cleared. (This comment used to name a count. Counts drift
 * the moment a field is added; the rule does not.)
 */
export function updateRosterRow(
  projectId: string,
  rowId: string,
  input: RosterInput,
): Promise<RosterRow> {
  return apiClient
    .patch<RosterRow>(
      `${base(projectId)}/roster/${encodeURIComponent(rowId)}`,
      input,
    )
    .then((r) => r.data);
}

export function deleteRosterRow(
  projectId: string,
  rowId: string,
): Promise<void> {
  return apiClient
    .delete<void>(`${base(projectId)}/roster/${encodeURIComponent(rowId)}`)
    .then(() => undefined);
}

export interface RosterImportInput {
  mode: RosterImportMode;
  body: string;
  /** Parse and report without writing — what the modal draws before the
   *  operator has agreed to anything. */
  preview?: boolean;
}

export function importRoster(
  projectId: string,
  input: RosterImportInput,
): Promise<RosterImportResult> {
  return apiClient
    .post<RosterImportResult>(`${base(projectId)}/roster/import`, input)
    .then((r) => r.data);
}

/**
 * Save the whole table as a file. It goes through fileDownloader rather than
 * apiClient because the answer is text/csv, and apiClient discards anything
 * that is not JSON.
 */
export function exportRosterCSV(projectId: string): Promise<string> {
  return fileDownloader.download(
    `${base(projectId)}/roster.csv`,
    "bot-roster.csv",
  );
}

/**
 * The simulation's own document, and its own etag.
 *
 * `config` is `bot_settings.config` verbatim — no `bots` key wrapped round it,
 * because the section IS the document. The etag comes from the row's own
 * `updated_at`, which is why a save on the Config page no longer knocks this
 * page's precondition out and vice versa.
 */
export interface BotSettingsResponse {
  config: unknown;
  etag: string;
}

export async function getBotSettings(
  projectId: string,
): Promise<BotSettingsResponse> {
  const r = await apiClient.get<{ config: unknown }>(
    `${base(projectId)}/settings`,
  );
  return { config: r.data.config, etag: stripETag(r.etag) };
}

/**
 * Patch the document — an RFC 7386 merge patch, so one decision can travel as
 * one word.
 *
 * That is what «Where a new bot comes from» sends (`{"source":"table"}`) and
 * what the page header's simulation switch sends (`{"enabled":true}`), neither
 * of them carrying the balance sheet beside it. The Settings tab sends the diff
 * of its whole form — see mergePatch, and why a diff rather than the document.
 */
export async function patchBotSettings(
  projectId: string,
  patch: BotSettingsPatch,
  etag: string,
): Promise<BotSettingsResponse> {
  const r = await apiClient.patch<{ config: unknown }>(
    `${base(projectId)}/settings`,
    patch,
    etag,
  );
  return { config: r.data.config, etag: stripETag(r.etag) };
}

/**
 * The etag as the next If-Match needs it: the header arrives quoted, and
 * possibly weak, and apiClient quotes whatever it is handed.
 */
function stripETag(header: string | null): string {
  return (header ?? "").replace(/^W\//, "").replace(/^"|"$/g, "");
}

/**
 * The refusals of the roster endpoints, as sentences.
 *
 * `botError` above cannot do this job: `invalid_row` is a 400 whose whole
 * value is the server's own message — «stat:level — card: value does not match
 * the declared field» is a field an operator can go and fix, and the generic
 * "Something went wrong" this would otherwise become is not.
 */
export function rosterError(err: unknown): string {
  if (!(err instanceof ApiError)) return describeError("internal");
  if (err.code === "invalid_row") {
    const reason = errorReason(err);
    return reason
      ? capitalise(reason.replace(ROSTER_SENTINEL, ""))
      : "This row is not valid for this project.";
  }
  return ROSTER_ERRORS[err.code] ?? describeError(err.code);
}

/** The Go sentinel `err.Error()` carries in front of the part that matters —
 *  bot.ErrInvalidRosterRow, verbatim. */
const ROSTER_SENTINEL = /^bot: roster row is not valid:\s*/;

const ROSTER_ERRORS: Record<string, string> = {
  row_spent:
    "This row has already become a bot, and a table cannot un-create one. Remove the bot on the Pool tab instead.",
  name_taken: "Another row of this table already carries that name.",
  invalid_source: "Unknown bot source — reload the page and try again.",
  body_too_large: BODY_TOO_LARGE,
};

function capitalise(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/* --- the Guild templates tab: bot_guild_templates and the guilds it stands
       up ------------------------------------------------------------------ */

export interface GuildTemplateParams {
  q?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * One page of the table plus the three stat cards above it. They travel
 * together because the tab draws them together — the handler says the same
 * thing from the other side.
 */
export function listGuildTemplates(
  projectId: string,
  params: GuildTemplateParams = {},
): Promise<GuildTemplatesResponse> {
  const suffix = query(params);
  return apiClient
    .get<GuildTemplatesResponse>(`${base(projectId)}/guild-templates${suffix}`)
    .then((r) => r.data);
}

export function createGuildTemplate(
  projectId: string,
  input: GuildTemplateInput,
): Promise<GuildTemplate> {
  return apiClient
    .post<GuildTemplate>(`${base(projectId)}/guild-templates`, input)
    .then((r) => r.data);
}

/**
 * Replace one unused row's three editable fields. A row that has already
 * become a guild is refused with `template_spent`: the row is the only record
 * of where that guild came from, and the admin guilds list reads it.
 */
export function updateGuildTemplate(
  projectId: string,
  templateId: string,
  input: GuildTemplateInput,
): Promise<GuildTemplate> {
  return apiClient
    .patch<GuildTemplate>(
      `${base(projectId)}/guild-templates/${encodeURIComponent(templateId)}`,
      input,
    )
    .then((r) => r.data);
}

export function deleteGuildTemplate(
  projectId: string,
  templateId: string,
): Promise<void> {
  return apiClient
    .delete<void>(
      `${base(projectId)}/guild-templates/${encodeURIComponent(templateId)}`,
    )
    .then(() => undefined);
}

/**
 * «Create this guild again» — un-spend a row so it can be used a second time.
 *
 * A POST on a sub-path rather than a PATCH clearing a field, because it is not
 * an edit of the row: a disbanded guild leaves its row spent on purpose, and
 * this is the deliberate click that takes that back.
 */
export function freeGuildTemplate(
  projectId: string,
  templateId: string,
): Promise<void> {
  return apiClient
    .post<void>(
      `${base(projectId)}/guild-templates/${encodeURIComponent(templateId)}/free`,
    )
    .then(() => undefined);
}

export interface GuildTemplateImportInput {
  mode: RosterImportMode;
  body: string;
  /** Parse and report without writing — what the modal draws before the
   *  operator has agreed to anything. */
  preview?: boolean;
}

export function importGuildTemplates(
  projectId: string,
  input: GuildTemplateImportInput,
): Promise<GuildTemplateImportResult> {
  return apiClient
    .post<GuildTemplateImportResult>(
      `${base(projectId)}/guild-templates/import`,
      input,
    )
    .then((r) => r.data);
}

/** The whole table as a file, spent rows included — see exportRosterCSV for
 *  why this goes through fileDownloader and not apiClient. */
export function exportGuildTemplatesCSV(projectId: string): Promise<string> {
  return fileDownloader.download(
    `${base(projectId)}/guild-templates.csv`,
    "bot-guild-templates.csv",
  );
}

/** Draw a create-guilds plan and, unless `preview`, carry it out. A run the
 *  project cannot pay for is refused with `not_enough_source` — see
 *  refusedPlan for the numbers that refusal carries. */
export function createGuilds(
  projectId: string,
  input: CreateGuildsInput,
): Promise<CreateGuildsPlan> {
  return apiClient
    .post<CreateGuildsPlan>(
      `${base(projectId)}/guild-templates/create-guilds`,
      input,
    )
    .then((r) => r.data);
}

/**
 * The plan a refused run came back with, or null for every other failure.
 *
 * `not_enough_source` means the project cannot pay for the run — the source has
 * fewer rows than the plan needs bots, or fewer usable templates than it asks
 * for guilds — and the 409 carries THE PLAN in `error.details`. The dialog's
 * red alert prints two numbers off it, «the source has 34 rows and this needs
 * 41 bots»: `rows_left` and `total_bots`, as the server drew them. Rebuilding
 * that sentence out of the message string would be the browser parsing English.
 *
 * A details bag that is not a plan reads as no plan rather than throwing: the
 * caller falls back to the sentence in guildTemplateError, which is a worse
 * alert but still an alert. Same rule as errorProblems.
 */
export function refusedPlan(err: unknown): CreateGuildsPlan | null {
  if (!(err instanceof ApiError) || err.code !== "not_enough_source") {
    return null;
  }
  const details = err.details;
  if (!details || typeof details !== "object") return null;
  const bag = details as Record<string, unknown>;
  if (!Array.isArray(bag.guilds) || typeof bag.total_bots !== "number") {
    return null;
  }
  return details as CreateGuildsPlan;
}

/**
 * The refusals of the guild-template endpoints, as sentences.
 *
 * The two 400s are the reason this is not `botError`: their whole value is the
 * server's own message. «9 members is more than guild.max_members (8)» names a
 * field an operator can go and change, and "Something went wrong" is what that
 * would otherwise become.
 */
export function guildTemplateError(err: unknown): string {
  if (!(err instanceof ApiError)) return describeError("internal");
  if (err.code === "invalid_template" || err.code === "invalid_plan") {
    const reason = errorReason(err);
    return reason
      ? capitalise(reason.replace(TEMPLATE_SENTINEL, ""))
      : "This guild template is not valid for this project.";
  }
  return TEMPLATE_ERRORS[err.code] ?? describeError(err.code);
}

/**
 * The Go sentinels `err.Error()` carries in front of the part that matters —
 * bot.ErrInvalidGuildTemplate and bot.ErrInvalidGuildPlan, verbatim. The
 * leading `.*?` is for the call that wraps its sentinel in a method name
 * («bot.CreateGuilds: bot: the guild plan is not valid: …»).
 */
const TEMPLATE_SENTINEL =
  /^.*?bot: (?:invalid guild template|the guild plan is not valid):\s*/;

const TEMPLATE_ERRORS: Record<string, string> = {
  name_exists: "This project already has a guild template with that name.",
  // Only reached when the refusal's plan could not be read — the dialog draws
  // its own alert out of `error.details` whenever it is there.
  not_enough_source:
    "The project cannot pay for this run — lower the numbers or put more rows into the source.",
  // The source running out DURING a run. It is the ordinary mid-run shortage
  // and it is UNDONE: the guilds are dissolved, the bots deleted and the rows
  // freed before this answer is written, so «nothing was created» is the whole
  // truth. The one run that leaves guilds standing answers 200 with `partial`
  // and never reaches here.
  roster_exhausted:
    "The table ran out of rows while the guilds were being created, so nothing was created. Import more rows, or ask for fewer guilds.",
  name_pool_exhausted:
    "The name pool ran dry while the guilds were being created, so nothing was created. Add more names to the pool, or ask for fewer guilds.",
  template_spent:
    "This template has already become a guild. Free it with «Create this guild again» before reusing it.",
  no_guild_founder:
    "This deployment cannot stand guilds up — no founder is wired into it. Everything else on this tab still works.",
  body_too_large: BODY_TOO_LARGE,
};
